import React, { createContext, useContext, useState, useEffect } from "react";
import jwtDecode from "jwt-decode";
import axios from "axios";
const AuthContext = createContext(null);
const safeJSONParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const US_DEFAULTS = {
  CountryCode: "US",
  CountryID: 1,
  CountryName: "United States",
  CurrencySymbol: "$",
  DateFormat: "MM/dd/yyyy",
  GMTOffset: "UTC-04:00",
  HasMultipleTimezones: true,
  MobileCode: "+1",
  NumberFormat: "1,234,567.89",
  State: "California",
  City: "Los Angeles",
};
export const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded.userId || null;
  } catch (error) {
    console.error("Error getting userId:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [locationSettings, setLocationSettings] = useState(() => {
    return safeJSONParse(localStorage.getItem("locationSettings")) || US_DEFAULTS;
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tokenInStorage = localStorage.getItem("token");
    if (!tokenInStorage) {
      setLoading(false);
      return;
    }
    if (tokenInStorage) {
      try {
        const decoded = jwtDecode(tokenInStorage);
        setToken(tokenInStorage);
        setUser(decoded);
        // fetchUserLocationSettings(decoded);
      } catch (error) {
        console.error("Invalid token in storage", error);
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!token) return;

      const decoded = jwtDecode(token);
      const fullUser = await fetchFullUser(decoded.userId);
      if (!fullUser) return;

      const normalizedUser = {
        userId: fullUser.ID || fullUser.userId,
        firstName: fullUser.FirstName,
        lastName: fullUser.LastName,
        email: fullUser.Email,
        city: fullUser.City,
        state: fullUser.State,
        country: fullUser.Country,
        ...fullUser,
      };

      const details = fullUser.CountryDetails || {};

      // FALLBACK if ANY essential field is NULL/empty
      const isMissing =
        !normalizedUser.country ||
        !normalizedUser.state ||
        !normalizedUser.city ||
        !details.CurrencySymbol ||
        !details.DateFormat;

      const finalSettings = isMissing ? US_DEFAULTS : details;

      // Save cleanly only once
      setLocationSettings(finalSettings);
      localStorage.setItem("locationSettings", JSON.stringify(finalSettings));

      // Update normalized user with location fallback
      if (isMissing) {
        normalizedUser.country = US_DEFAULTS.CountryName;
        normalizedUser.state = US_DEFAULTS.State;
        normalizedUser.city = US_DEFAULTS.City;
      }

      setUser(normalizedUser);
    };

    loadUserProfile();
  }, [token]);

  const fetchFullUser = async (userId) => {
    try {
      const token = await localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/User/Get/${userId}`, { headers: { Authorization: `Bearer ${token}` }, }
      );
      return res.data;
    } catch (err) {
      console.error("Failed to fetch full user:", err);
      return null;
    }
  };
  const getUserCurrencySymbol = () => {
    return locationSettings?.CurrencySymbol || "$";
  };


  console.log("locationSettings", locationSettings);

  const login = (tokenStr, userInfo) => {
    setToken(tokenStr);
    setUser(userInfo);
    localStorage.setItem("token", tokenStr);

  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("locationSettings");
  };
  const updateUser = (updatedFields) => {
    setUser((prev) => ({ ...prev, ...updatedFields }));
  };

  // Helper functions to get user information
  const getUserId = () => {
    return user ? user.userId : null;
  };

  const getUserFirstName = () => {
    return user ? user.firstName : "";
  };

  const getUserLastName = () => {
    return user ? user.lastName : "";
  };

  const getUserFullName = () => {
    return user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
  };

  const getUserEmail = () => {
    return user ? user.email : "";
  };

  const getUserCity = () => {
    return user ? user.city : "";
  };

  const getUserState = () => {
    return user ? user.state : "";
  };

  const getUserCountry = () => {
    return user ? user.country : "";
  };

  const getUserLocation = () => {
    if (!user) return "";
    const parts = [user.city, user.state, user.country].filter(Boolean);
    return parts.join(", ");
  };

  const isAuthenticated = () => {
    return !!token;
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        loading,
        locationSettings,
        // Helper functions
        getUserId,
        getUserFirstName,
        getUserLastName,
        getUserFullName,
        getUserEmail,
        getUserCity,
        getUserState,
        getUserCurrencySymbol,
        getUserCountry,
        getUserLocation,
        isAuthenticated,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
